import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import Bubbles from '../components/Bubbles';
import { useBubbleTransition } from '../components/BubbleTransition';

// ─── CONFIG ───────────────────────────────────────────────────────────────────
const APPS_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbwBBHBsSHMcKGWsS5t_db-3tU-fs2kk8MOA0FCP0QcvklK8NcqQWlsSbIEX0ugxYvVN/exec';
const SECRET_TOKEN    = 'UkY3gtsl36QgCRPDIlIStQSh7EYj8C2Q9cceGOrzEMBiRxMaIFFoE5B1XatuMhYS6CqTJvuxoLIWFaWWpFPQbXZ';
const REGISTRATION_OPEN = true;
const MAX_TEAM_SIZE = 3;
// ─────────────────────────────────────────────────────────────────────────────

const CREW_LABELS = ['First Mate', 'Navigator', 'Deck Hand'];
const emptyMember = () => ({ name: '', email: '', isIeeeMember: '' });
const createEmptyMembers = () => Array.from({ length: MAX_TEAM_SIZE - 1 }, emptyMember);

function Register() {
  const [formData, setFormData] = useState({
    name: '', phone: '', university: '', email: '',
    isIeeeMember: '', participationType: '', teamName: '', _honey: '',
  });
  const [teamSize, setTeamSize] = useState(1);
  const [members,  setMembers]  = useState(createEmptyMembers());
  const [status,   setStatus]   = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const trigger  = useBubbleTransition();
  const navigate = useNavigate();

  const handleChange = (e) =>
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleMemberChange = (index, field, value) => {
    setMembers(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData._honey) return;

    if (teamSize < 1 || teamSize > MAX_TEAM_SIZE) {
      setErrorMsg(`Team size must be between 1 and ${MAX_TEAM_SIZE}.`);
      return;
    }

    if (!formData.isIeeeMember || !formData.participationType) {
      setErrorMsg("Please answer all the questions before boarding! 🐚");
      return;
    }

    for (let i = 0; i < teamSize - 1; i++) {
      const m = members[i];
      if (!m.name.trim() || !m.email.trim() || !m.isIeeeMember) {
        setErrorMsg(`Please fill in all fields for Crewmate ${i + 2}! 🐚`);
        return;
      }
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const payload = {
        name:              formData.name.trim(),
        phone:             formData.phone.trim(),
        university:        formData.university.trim(),
        email:             formData.email.trim(),
        isIeeeMember:      formData.isIeeeMember,
        participationType: formData.participationType,
        teamName:          formData.teamName.trim(),
        teamSize,
        members: members.slice(0, teamSize - 1).map(m => ({
          name: m.name.trim(), email: m.email.trim(), isIeeeMember: m.isIeeeMember,
        })),
        _honey:    formData._honey,
        timestamp: new Date().toISOString(),
        _token:    SECRET_TOKEN,
      };

      const response = await fetch(APPS_SCRIPT_URL, {
        method: 'POST', headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(payload),
      });

      const data = JSON.parse(await response.text());

      if (data.status === 'success') {
        setStatus('success');
        setFormData({ name:'', phone:'', university:'', email:'', isIeeeMember:'', participationType:'', teamName:'', _honey:'' });
        setTeamSize(1);
        setMembers(createEmptyMembers());
      } else {
        throw new Error(data.message || 'Unknown error');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || "Something went wrong. Please try again! 🌊");
    }
  };

  const goHome = () => trigger(() => navigate('/'));

  // ── REGISTRATION CLOSED ────────────────────────────────────────────────────
  if (!REGISTRATION_OPEN) {
    return (
      <div className="register-page">
        <SEOHelmet 
          title="Register - Code It Up 6.0 Hackathon"
          description="Register your team for Code It Up 6.0 hackathon. Join the 24-hour coding competition and showcase your programming and AI skills."
          keywords="register hackathon, code it up registration, join hackathon, coding competition signup"
          canonical="https://code-it-up-6.netlify.app/register"
        />
        <Bubbles />
        <div className="register-content">
          <div className="reg-closed-box">
            <div className="reg-closed-icon">🔒</div>
            <h1 className="reg-closed-title">Registration Closed</h1>
            <p className="reg-closed-sub">The ship hasn't set sail yet! 🚢</p>
            <p className="reg-closed-desc">
              Registration isn't open yet. Stay tuned — we'll drop anchor when it's time to board!
            </p>
          </div>
          <button className="register-back-btn" onClick={goHome}>← Back to Bikini Bottom</button>
        </div>
      </div>
    );
  }

  // ── MAIN FORM ──────────────────────────────────────────────────────────────
  return (
    <div className="register-page">
      <SEOHelmet 
        title="Register - Code It Up 6.0 Hackathon"
        description="Register your team for Code It Up 6.0 hackathon. Join the 24-hour coding competition and showcase your programming and AI skills."
        keywords="register hackathon, code it up registration, join hackathon, coding competition signup"
        canonical="https://code-it-up-6.netlify.app/register"
      />
      <Bubbles />
      <div className="register-content">
        <h1 className="register-page-title">REGISTER NOW</h1>
        <p className="register-page-subtitle">Grab your net and join the underwater coding adventure!</p>

        {status === 'success' && (
          <div className="register-success">
            <div className="success-bubble">
              <h2>You're In! 🎉</h2>
              <p>"I'm ready! I'm ready! I'm ready!"</p>
              <p style={{ fontSize:'1rem', marginTop:'10px', opacity:0.85 }}>
                Your crew has been registered. See you at the event!
              </p>
            </div>
          </div>
        )}

        {status !== 'success' && (
          <form onSubmit={handleSubmit} className="register-form" noValidate>

            {/* Honeypot */}
            <div style={{ position:'absolute', left:'-9999px', opacity:0, pointerEvents:'none' }} aria-hidden="true">
              <input type="text" name="_honey" value={formData._honey} onChange={handleChange} tabIndex="-1" autoComplete="off" />
            </div>

            {/* ── CAPTAIN INFO ────────────────────────────────────── */}
            <div className="register-section-title">🧽 Captain's Info</div>

            <div>
              <label htmlFor="name">Full Name:</label>
              <input type="text" id="name" name="name" placeholder="Enter your full name"
                value={formData.name} onChange={handleChange} required autoComplete="name" />
            </div>

            <div>
              <label htmlFor="phone">🐚 Conch Shell Number (Phone):</label>
              <input type="tel" id="phone" name="phone" placeholder="e.g. +216 XX XXX XXX"
                value={formData.phone} onChange={handleChange} required autoComplete="tel" />
            </div>

            <div>
              <label htmlFor="university">🏫 University / School:</label>
              <input type="text" id="university" name="university" placeholder="Enter your university name"
                value={formData.university} onChange={handleChange} required autoComplete="organization" />
            </div>

            <div>
              <label htmlFor="email">✉️ Message in a Bottle (Email):</label>
              <input type="email" id="email" name="email" placeholder="Enter your email address"
                value={formData.email} onChange={handleChange} required autoComplete="email" />
            </div>

            <div className="register-toggle-group">
              <label className="register-toggle-label">🔵 Are you an IEEE member?</label>
              <div className="register-toggle-options">
                {['Yes','No'].map(opt => (
                  <button key={opt} type="button"
                    className={`register-toggle-btn ${formData.isIeeeMember === opt ? 'active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, isIeeeMember: opt }))}>
                    {opt === 'Yes' ? '✅ Yes' : '❌ No'}
                  </button>
                ))}
              </div>
            </div>

            <div className="register-toggle-group">
              <label className="register-toggle-label">🏆 How will you participate?</label>
              <div className="register-toggle-options">
                {[
                  { value: 'Hackathon', label: '⚔️ Hackathon Competitor' },
                  { value: 'Visitor',   label: '👀 Visitor / Spectator'  },
                ].map(opt => (
                  <button key={opt.value} type="button"
                    className={`register-toggle-btn ${formData.participationType === opt.value ? 'active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, participationType: opt.value }))}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── CREW SIZE ───────────────────────────────────────── */}
            <div className="crew-section">
              <div className="register-section-title">🐟 Crew Size</div>
              <div className="crew-count-selector">
                {[1,2,3].map(num => (
                  <button key={num} type="button"
                    className={`crew-count-btn ${teamSize === num ? 'active' : ''}`}
                    onClick={() => setTeamSize(Math.min(num, MAX_TEAM_SIZE))}>
                    {num === 1 ? '🧍 Solo' : `👥 ${num}`}
                  </button>
                ))}
              </div>
            </div>

            {/* ── CREW NAME (shown when team > 1) ─────────────────── */}
            <div>
                <label htmlFor="teamName">🏴‍☠️ Crew Name (Team Name):</label>
                <input type="text" id="teamName" name="teamName"
                  placeholder="e.g. The Krusty Koders"
                  value={formData.teamName} onChange={handleChange} />
              </div>

            {/* ── EXTRA CREW MEMBERS ──────────────────────────────── */}
            {teamSize > 1 && (
              <div className="crew-members-section">
                <p className="crew-members-label">🧽 Your Crewmates:</p>
                {Array.from({ length: teamSize - 1 }, (_, i) => (
                  <div key={i} className="crew-member-card">
                    <div className="crew-member-header">{CREW_LABELS[i]} #{i + 2}</div>

                    <div className="crew-member-input">
                      <label htmlFor={`m-name-${i}`}>Full Name:</label>
                      <input type="text" id={`m-name-${i}`} placeholder={`Crewmate ${i+2}'s name`}
                        value={members[i].name} onChange={e => handleMemberChange(i, 'name', e.target.value)} />
                    </div>

                    <div className="crew-member-input">
                      <label htmlFor={`m-email-${i}`}>Email:</label>
                      <input type="email" id={`m-email-${i}`} placeholder={`Crewmate ${i+2}'s email`}
                        value={members[i].email} onChange={e => handleMemberChange(i, 'email', e.target.value)} />
                    </div>

                    <div className="register-toggle-group" style={{ marginBottom: 0 }}>
                      <label className="register-toggle-label" style={{ fontSize: '1rem' }}>🔵 IEEE member?</label>
                      <div className="register-toggle-options">
                        {['Yes','No'].map(opt => (
                          <button key={opt} type="button"
                            className={`register-toggle-btn ${members[i].isIeeeMember === opt ? 'active' : ''}`}
                            onClick={() => handleMemberChange(i, 'isIeeeMember', opt)}>
                            {opt === 'Yes' ? '✅ Yes' : '❌ No'}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {errorMsg && <div className="register-error-msg">⚠️ {errorMsg}</div>}

            <button type="submit" disabled={status === 'loading'}>
              {status === 'loading' ? '🌊 Sending...' : '🚢 Board the Ship!'}
            </button>
          </form>
        )}

        <button className="register-back-btn" onClick={goHome}>← Back to Bikini Bottom</button>
      </div>
    </div>
  );
}

export default Register;
